import React, { createContext, useState, useEffect, useContext, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { subscribeToFamily } from '@coparent/shared/src/services/familyService';
import { subscribeToChildren } from '@coparent/shared/src/services/childService';
import { DEFAULT_CHILD_COLORS } from '@coparent/shared/src/utils/constants';

const FamilyContext = createContext();

export const useFamily = () => {
  const context = useContext(FamilyContext);
  if (!context) {
    throw new Error('useFamily must be used within a FamilyProvider');
  }
  return context;
};

export const FamilyProvider = ({ children }) => {
  const { userData, currentUser } = useAuth();
  const [family, setFamily] = useState(null);
  const [familyChildren, setFamilyChildren] = useState([]);
  const [loading, setLoading] = useState(true);

  // The family this user claims to belong to, per their own user doc. Used to
  // read the family document (permitted for any authenticated user) and to
  // drive routing/onboarding via `hasFamily`. NOT used directly to subscribe to
  // member-gated subcollections — see `familyId` below.
  const userFamilyId = userData?.familyIds?.[0] || null;

  useEffect(() => {
    if (!userFamilyId) {
      setFamily(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    const unsubFamily = subscribeToFamily(userFamilyId, (data) => {
      setFamily(data);
      setLoading(false);
    });

    return () => {
      unsubFamily();
    };
  }, [userFamilyId]);

  // Whether the loaded family document actually lists the current user as a
  // member (parent or household member). Subcollection security rules
  // (`isAnyFamilyMember`) require this. Right after a co-parent joins, their
  // user-doc `familyIds` update can reach this client a beat before the
  // family-doc membership write does; subscribing to subcollections in that
  // window draws a permission-denied, and a denied onSnapshot listener is
  // terminal — it stays dead until the component remounts. Gating subscriptions
  // on confirmed membership closes that race.
  const isConfirmedMember = !!(
    family &&
    currentUser &&
    family.id === userFamilyId &&
    (family.parentIds?.includes(currentUser.uid) ||
      family.householdMembers?.[currentUser.uid])
  );

  // Family id used for member-gated subcollection subscriptions and writes.
  // Null until membership is confirmed in the family document, so consumers
  // (Dashboard, Activities, Expenses, useNotifications, …) don't subscribe
  // before the join has fully propagated.
  const familyId = isConfirmedMember ? userFamilyId : null;

  useEffect(() => {
    if (!familyId) {
      setFamilyChildren([]);
      return;
    }

    const unsubChildren = subscribeToChildren(familyId, (data) => {
      const normalized = data.map(child => ({
        ...child,
        name: child.name || child.firstName,
        firstName: child.firstName || child.name,
      }));
      setFamilyChildren(normalized);
    });

    return () => {
      unsubChildren();
    };
  }, [familyId]);

  const getCoParent = () => {
    if (!family || !currentUser) return null;
    const coParentId = family.parentIds.find(id => id !== currentUser.uid);
    if (!coParentId) return null;
    return {
      id: coParentId,
      ...family.parentProfiles[coParentId]
    };
  };

  const getCurrentParentProfile = () => {
    if (!family || !currentUser) return null;
    // Check primary parents first
    if (family.parentProfiles[currentUser.uid]) {
      return {
        id: currentUser.uid,
        ...family.parentProfiles[currentUser.uid]
      };
    }
    // Check household members
    const members = family.householdMembers || {};
    if (members[currentUser.uid]) {
      return {
        id: currentUser.uid,
        ...members[currentUser.uid]
      };
    }
    return null;
  };

  const getFamilyDisplayName = () => {
    if (!family) return '';
    if (!currentUser) return family.name || '';
    const profile = family.parentProfiles?.[currentUser.uid];
    return profile?.familyName || family.name || '';
  };

  const getParticipantProfile = (id) => {
    if (!family) return null;
    if (family.parentProfiles[id]) {
      return { id, ...family.parentProfiles[id] };
    }
    const members = family.householdMembers || {};
    if (members[id]) {
      return { id, ...members[id] };
    }
    return null;
  };

  const getAllParticipants = () => {
    if (!family) return [];
    const participants = [];
    // Primary parents first
    family.parentIds.forEach(pid => {
      const profile = family.parentProfiles[pid];
      if (profile) {
        participants.push({ id: pid, ...profile, householdOf: null });
      }
    });
    // Then household members
    const members = family.householdMembers || {};
    Object.entries(members).forEach(([mid, member]) => {
      participants.push({ id: mid, ...member });
    });
    return participants;
  };

  const getHouseholdMembers = (parentId) => {
    if (!family) return [];
    const members = family.householdMembers || {};
    return Object.entries(members)
      .filter(([, m]) => m.householdOf === parentId)
      .map(([id, m]) => ({ id, ...m }));
  };

  const isHouseholdMember = !!(
    family && currentUser &&
    !family.parentIds.includes(currentUser.uid) &&
    family.householdMembers?.[currentUser.uid]
  );

  const currentUserHousehold = isHouseholdMember
    ? family.householdMembers[currentUser.uid]?.householdOf
    : null;

  const childrenMap = useMemo(() => {
    const map = {};
    familyChildren.forEach((child, index) => {
      map[child.id] = {
        ...child,
        color: child.color || DEFAULT_CHILD_COLORS[index % DEFAULT_CHILD_COLORS.length].hex
      };
    });
    return map;
  }, [familyChildren]);

  const value = {
    family,
    familyId,
    familyChildren,
    childrenMap,
    getCoParent,
    getFamilyDisplayName,
    getCurrentParentProfile,
    getParticipantProfile,
    getAllParticipants,
    getHouseholdMembers,
    isHouseholdMember,
    currentUserHousehold,
    loading,
    hasFamily: !!userFamilyId
  };

  return (
    <FamilyContext.Provider value={value}>
      {children}
    </FamilyContext.Provider>
  );
};
